import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Hint } from 'react-autocomplete-hint'
import { isValid, parse, set } from 'date-fns'
import { ParseableDate } from '@mui/lab/internal/pickers/constants/prop-types'
import { BasePickerProps } from '@mui/lab/internal/pickers/typings/BasePicker'
import { containerStyles, helperTextStyles, inputStyles, labelStyles } from './index.styles'
import { theme } from '../../../modules/App/Providers/Theme/theme'

type Props = BasePickerProps<ParseableDate<Date>, Date | null> & {
  hasError?: boolean
  helperText?: string
  label: string
  name: string
  onBlur?: () => void
  onChange?: (date: Date | null, keyboardInputValue?: string | undefined) => void
  onFocus?: () => void
  value: Date | null
}

const parseTimeString = (timeString: string): Date => {
  const time = parse(timeString, 'h:mm a', new Date())
  return set(new Date(), {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  })
}

const HintInput = forwardRef(({ hasError, helperText, label, name, onBlur, onChange, onFocus }: Props) => {
  const placeholder = 'hh:mm (a|p)m'
  const [inputValue, setInputValue] = useState<string | undefined>()
  const [inputStyle, setInputStyle] = useState(inputStyles)
  const [labelStyle, setLabelStyle] = useState(labelStyles)

  const onChangeProxy = useCallback(
    (keyboardInputValue?: string | undefined) => {
      if (keyboardInputValue) {
        const blankSpacesCount = keyboardInputValue?.split(' ').length - 1

        // ignore extra white spaces
        if (blankSpacesCount > 1) {
          return
        }
      }

      setInputValue(keyboardInputValue)

      if (onChange) {
        const date = parseTimeString(keyboardInputValue as string)
        if (isValid(date)) {
          date?.setMilliseconds(0)
          onChange(date, keyboardInputValue as string)
        } else {
          onChange(null)
        }
      }
    },
    [onChange]
  )

  const getHint = (e: string) => {
    const completeHintString = String(placeholder)
    let sliceUntilCharAt = 0

    if (e.includes('a') || e.includes('p')) {
      sliceUntilCharAt += 4
    }

    return completeHintString.slice(0, e.length + sliceUntilCharAt)
  }

  const handleFocus = () => {
    setInputStyle({
      ...inputStyle,
      outline: 'none',
      borderWidth: '2px',
      borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
    })

    setLabelStyle({ ...labelStyle, color: hasError ? theme.palette.error.main : theme.palette.primary.main })

    if (onFocus) onFocus()
  }

  const handleBlur = () => {
    setInputStyle({
      ...inputStyle,
      outline: 'none',
      borderWidth: '1px',
      borderColor: hasError ? theme.palette.error.main : '#c0c0c0',
    })

    setLabelStyle({ ...labelStyle, color: hasError ? theme.palette.error.main : labelStyles.color })

    if (onBlur) onBlur()
  }

  const handleMouseOver = () => {
    setInputStyle({
      ...inputStyle,
      borderColor: hasError ? theme.palette.error.main : 'black',
    })
  }

  const handleMouseOut = () => {
    setInputStyle({
      ...inputStyle,
      borderColor: hasError ? theme.palette.error.main : '#c0c0c0',
    })
  }

  useEffect(() => {
    if (hasError) {
      setInputStyle({
        ...inputStyle,
        borderColor: theme.palette.error.main,
      })

      setLabelStyle({ ...labelStyle, color: theme.palette.error.main })
    } else {
      setInputStyle({
        ...inputStyle,
        borderColor: inputStyles.color,
      })

      setLabelStyle({ ...labelStyle, color: labelStyles.color })
    }
  }, [hasError])

  return (
    <div style={containerStyles}>
      <Hint options={[String(placeholder)]} valueModifier={(e) => getHint(e)}>
        <input
          id="hintInput"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          name={name}
          value={inputValue}
          placeholder={placeholder}
          style={inputStyle}
          onChange={(e) => onChangeProxy(e.target.value)}
        />
      </Hint>

      <label htmlFor="hintInput" style={labelStyle}>
        <div>{label}</div>
      </label>

      <span style={helperTextStyles}>{helperText}</span>
    </div>
  )
})

export type { Props as HintInputProps }
export { HintInput }
